import { useEffect, useState, useRef, useCallback } from "react";
import { Form, Modal } from "antd";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import dayjs from "dayjs";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMAS } from "@/constants/schemas";
import { getOptionsFromRefList, generateRules } from "@/util/common_function";
import { MODULES_LIST } from "@/util/common_variable";

const WarrantInformations = ({ locateInfoState, form: formProp }) => {
  const [localForm] = Form.useForm();
  const form = formProp ?? localForm;
  const { activeWarrant, bookingsFieldsEnabled, patch } = useActiveWarrant();

  const [status, setStatus] = useState();
  const juvenile = Form.useWatch("juvenileWarrant", form);

  // Debounce refs — one per text field that patches Redux
  const debounceDocket      = useRef(null);
  const debounceWarrantNum  = useRef(null);
  const debounceWaitComment = useRef(null);
  const debounceClearComment= useRef(null);

  // Stable ref to activeWarrant so debounced callbacks always see latest value
  // without being recreated (which would reset the debounce timer)
  const activeWarrantRef = useRef(activeWarrant);
  useEffect(() => { activeWarrantRef.current = activeWarrant; }, [activeWarrant]);

  const apiDateToDayjs = (val) => {
    if (!val) return null;
    if (Array.isArray(val)) return dayjs(new Date(val[0], val[1] - 1, val[2]));
    const d = dayjs(val);
    return d.isValid() ? d : null;
  };

  const apiDateTimeToDayjs = (dateVal, timeVal) => {
    if (!dateVal) return null;
    const datePart = Array.isArray(dateVal)
      ? `${dateVal[0]}-${String(dateVal[1]).padStart(2, "0")}-${String(dateVal[2]).padStart(2, "0")}`
      : dateVal;
    let timePart = "00:00:00";
    if (timeVal) {
      if (typeof timeVal === "object" && "hour" in timeVal) {
        timePart = `${String(timeVal.hour ?? 0).padStart(2, "0")}:${String(timeVal.minute ?? 0).padStart(2, "0")}:${String(timeVal.second ?? 0).padStart(2, "0")}`;
      } else if (typeof timeVal === "string") {
        timePart = timeVal;
      }
    }
    const d = dayjs(`${datePart} ${timePart}`);
    return d.isValid() ? d : null;
  };

  // Hydrate form only when the warrant itself changes, not on every patch
  useEffect(() => {
    if (!activeWarrant) return;

    const waitForInfo   = activeWarrant?.warrantWaitForInfo?.[0];
    const clearanceInfo = activeWarrant?.warrantClearanceInfo?.[0];

    const statusOptions     = getOptionsFromRefList("Status", false, false, true);
    const outstandingStatus = statusOptions.find((o) => o.label === "OUTSTANDING");
    const statusId          = activeWarrant?.statusId ?? outstandingStatus?.value ?? null;

    form.setFieldsValue({
      sealed:             !!(activeWarrant?.sealed ?? false),
      serve247:           !!(activeWarrant?.serve247 ?? false),
      juvenileWarrant:    !!(activeWarrant?.juvenileWarrant ?? false),
      warrantNumber:      activeWarrant?.warrantNumber,
      docket:             activeWarrant?.docket,
      warrantType:        activeWarrant?.warrantType,
      dateIssued:         apiDateToDayjs(activeWarrant?.dateIssued),
      dateReceived:       apiDateToDayjs(activeWarrant?.dateReceived),
      dateProcessed:      apiDateToDayjs(activeWarrant?.dateProcessed),
      statusId,
      dateOfEmancipation: apiDateToDayjs(activeWarrant?.dateOfEmancipation),
      dateOfViolation:    apiDateToDayjs(activeWarrant?.dateOfViolation),
      waitForReasonId:    waitForInfo?.waitForReasonId ?? null,
      waitForDateTime:    apiDateTimeToDayjs(waitForInfo?.waitForDate,    waitForInfo?.waitForTime),
      waitForByStaffId:   waitForInfo?.waitForByStaffId  ? Number(waitForInfo.waitForByStaffId)  : null,
      waitForComments:    waitForInfo?.waitForComments   ?? null,
      clearanceReasonId:  clearanceInfo?.clearanceReasonId  ?? null,
      clearanceDateTime:  apiDateTimeToDayjs(clearanceInfo?.clearanceDate, clearanceInfo?.clearanceTime),
      clearedByStaffId:   clearanceInfo?.clearedByStaffId ? Number(clearanceInfo.clearedByStaffId) : null,
      clearanceComments:  clearanceInfo?.clearanceComments  ?? null,
    });

    if (activeWarrant?.statusId) {
      const matched = statusOptions.find((o) => o.value === activeWarrant.statusId);
      setStatus(matched?.label ?? null);
    } else {
      const matchedLabel = statusOptions.find((o) => o.label === "OUTSTANDING");
      form.setFieldsValue({ statusId: matchedLabel?.value ?? null });
      setStatus(matchedLabel?.label ?? null);
      patch({ statusId: matchedLabel?.value ?? null });
    }
  }, [activeWarrant?.warrantId]); // ← correct field name (not .id)

  // Keep status label in sync when statusId changes externally (e.g. arresting officer flow)
  useEffect(() => {
    if (!activeWarrant?.statusId) return;
    const statusOptions = getOptionsFromRefList("Status", false, false, true);
    const matched = statusOptions.find((o) => o.value === activeWarrant.statusId);
    if (matched) setStatus(matched.label);
  }, [activeWarrant?.statusId]);

  // Cleanup all debounce timers on unmount
  useEffect(() => {
    return () => {
      [debounceDocket, debounceWarrantNum, debounceWaitComment, debounceClearComment]
        .forEach((r) => { if (r.current) clearTimeout(r.current); });
    };
  }, []);

  // Immediate patch for non-text fields (dates, selects, checkboxes)
  const patchField = (key, val) => {
    const value = dayjs.isDayjs(val) ? val.format("YYYY-MM-DD HH:mm:ss") : val;
    patch({ [key]: value });
  };

  // Generic debounced text patch — 300ms
  const makeDebouncedPatch = (timerRef, buildPayload) => (value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      patch(buildPayload(value, activeWarrantRef.current));
    }, 300);
  };

  const patchDocket = makeDebouncedPatch(
    debounceDocket,
    (val) => ({ docket: val })
  );

  const patchWarrantNumber = makeDebouncedPatch(
    debounceWarrantNum,
    (val) => ({ warrantNumber: val })
  );

  const patchWaitForComments = makeDebouncedPatch(
    debounceWaitComment,
    (val, aw) => ({
      warrantWaitForInfo: [{
        ...(aw?.warrantWaitForInfo?.[0] ?? {}),
        waitForComments: val,
      }],
    })
  );

  const patchClearanceComments = makeDebouncedPatch(
    debounceClearComment,
    (val, aw) => ({
      warrantClearanceInfo: [{
        ...(aw?.warrantClearanceInfo?.[0] ?? {}),
        clearanceComments: val,
      }],
    })
  );

  // Immediate patch helpers for nested objects (selects inside wait-for / clearance)
  const patchWaitFor = (fields) => {
    patch({
      warrantWaitForInfo: [{
        ...(activeWarrantRef.current?.warrantWaitForInfo?.[0] ?? {}),
        ...fields,
      }],
    });
  };

  const patchClearance = (fields) => {
    patch({
      warrantClearanceInfo: [{
        ...(activeWarrantRef.current?.warrantClearanceInfo?.[0] ?? {}),
        ...fields,
      }],
    });
  };

  const showWarrantAlert = (message) => {
    Modal.warning({ title: "Invalid Input", content: message, okText: "OK" });
  };

  const handleWarrantNumberKeyDown = (e) => {
    const { key, ctrlKey, metaKey } = e;
    const allowedControlKeys = [
      "Backspace", "Delete", "ArrowLeft", "ArrowRight",
      "ArrowUp", "ArrowDown", "Tab", "Enter",
      "Home", "End", "Escape", "CapsLock", "Shift", "Control",
    ];
    if (allowedControlKeys.includes(key) || ctrlKey || metaKey) return;
    if (key.length > 1) return;
    if (e.target.value.length >= 15) {
      e.preventDefault();
      showWarrantAlert(
        "The Warrant number field allows alphanumeric characters only and must be 15 characters or less."
      );
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(key)) {
      e.preventDefault();
      showWarrantAlert(
        "The Warrant number field allows alphanumeric characters only and must be 15 characters or less."
      );
    }
  };

  const isWaitFor = ["WAIT FOR BODY", "WAIT FOR $"].includes(status);
  const isCleared = ["CLEARED"].includes(status);

  return (
    <>
      <StyledSectionHeading>
        <span>Warrant Information</span>
      </StyledSectionHeading>

      <FieldSecurityProvider
        entity={SCHEMAS.WARRANTS.SCHEMA_NAME}
        startingPoint={MODULES_LIST.WARRANTS}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form form={form} disabled={!bookingsFieldsEnabled}>
          <StyledFlex justify="end">
            <SecureFormItem
              fieldName={SCHEMAS.WARRANTS.FIELDS.SEALED}
              formItemProps={{
                label: "Sealed?",
                name: "sealed",
                valuePropName: "checked",
                layout: "horizontal",
                labelPostion: "label-right",
              }}
            >
              <Checkbox onChange={(e) => patchField("sealed", e.target.checked)} />
            </SecureFormItem>
          </StyledFlex>

          <StyledGridRow gutter={16}>
            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.WARRANT_NUMBER}
                formItemProps={{
                  label: "Warrant #",
                  name: "warrantNumber",
                  // Required rule stays static; the pattern rule (if any) comes from
                  // the Global Client Parameters "RegularExpression" / "Warrants:WarrantNumber"
                  // row — mirrors ASP's DataImporterInterface.asp ValidateRegularExpression.
                  rules: [
                    { required: true, message: "Warrant Number is required" },
                    ...generateRules("Warrants:WarrantNumber", "Warrant Number", false),
                  ],
                }}
              >
                <TextField
                  maxLength={15}
                  onKeyDown={handleWarrantNumberKeyDown}
                  onChange={(e) => patchWarrantNumber(e.target.value)}
                  className="field--pink"
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.DOCKET}
                formItemProps={{ label: "Docket/Pg", name: "docket" }}
              >
                <TextField onChange={(e) => patchDocket(e.target.value)} />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.WARRANT_TYPE}
                formItemProps={{ label: "Type", name: "warrantType" }}
              >
                <Select
                  options={getOptionsFromRefList("WarrantType")}
                  onChange={(v) => patchField("warrantType", v?.value || v)}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.DATE_ISSUED}
                formItemProps={{
                  label: "Issued",
                  name: "dateIssued",
                  rules: [{ required: true, message: "Issued Date is required" }],
                }}
              >
                <CommonDatePicker
                  className="field--pink"
                  format="MM/DD/YYYY"
                  showTime={false}
                  disabledDate={(current) => current && current.isAfter(dayjs(), "day")}
                  value={form.getFieldValue("dateIssued")}
                  onChange={(d) => {
                    form.setFieldsValue({ dateIssued: d });
                    patch({ dateIssued: d ? d.format("YYYY-MM-DD") : null });
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.DATE_RECEIVED}
                formItemProps={{ label: "Received", name: "dateReceived" }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY"
                  showTime={false}
                  disabledDate={(current) => current && current.isAfter(dayjs(), "day")}
                  value={form.getFieldValue("dateReceived")}
                  onChange={(d) => {
                    form.setFieldsValue({ dateReceived: d });
                    patch({ dateReceived: d ? d.format("YYYY-MM-DD") : null });
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.SERVE_24_7}
                formItemProps={{
                  label: "Serve 24/7",
                  name: "serve247",
                  valuePropName: "checked",
                  layout: "horizontal",
                  labelPostion: "label-right",
                }}
              >
                <Checkbox onChange={(e) => patchField("serve247", e.target.checked)} />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.DATE_PROCESSED}
                formItemProps={{ label: "Processed", name: "dateProcessed" }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY"
                  showTime={false}
                  disabledDate={(current) => current && current.isAfter(dayjs(), "day")}
                  value={form.getFieldValue("dateProcessed")}
                  onChange={(d) => {
                    form.setFieldsValue({ dateProcessed: d });
                    patch({ dateProcessed: d ? d.format("YYYY-MM-DD") : null });
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.STATUS_ID}
                formItemProps={{
                  label: "Status",
                  name: "statusId",
                  rules: [{ required: true, message: "Status is required" }],
                }}
              >
                <Select
                  options={getOptionsFromRefList("Status", false, false, true)}
                  onChange={(val, option) => {
                    setStatus(option?.label);
                    patchField("statusId", val);
                    locateInfoState?.(option?.label === "wait-for-body");

                    const now = dayjs();
                    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
                    const staffId = userDetails?.staffId || userDetails?.staffID || null;

                    if (option?.label === "CLEARED") {
                      form.setFieldsValue({
                        clearanceDateTime: now,
                        clearedByStaffId: staffId ? Number(staffId) : null,
                      });
                      patchClearance({
                        clearanceDate: now.format("MM/DD/YYYY"),
                        clearanceTime: now.format("HH:mm"),
                        clearedByStaffId: staffId ? Number(staffId) : null,
                      });
                    }

                    if (["WAIT FOR $", "WAIT FOR BODY"].includes(option?.label)) {

                      const waitForReasons = getOptionsFromRefList( "Warrant_WaitFor_Reason", false, false, true);

                      const matchingReason = waitForReasons.find(
                        (r) => r.label?.toUpperCase() === option.label?.toUpperCase()
                      );
                      form.setFieldsValue({
                        waitForReasonId: matchingReason?.value ?? null,
                        waitForDateTime: now,
                        waitForByStaffId: staffId ? Number(staffId) : null,
                      });
                      patchWaitFor({
                        waitForReasonId: matchingReason?.value ?? null,
                        waitForDate: now.format("MM/DD/YYYY"),
                        waitForTime: now.format("HH:mm"),
                        waitForByStaffId: staffId ? Number(staffId) : null,
                      });
                    }
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANTS.FIELDS.JUVENILE_WARRANT}
                formItemProps={{
                  name: "juvenileWarrant",
                  label: "Juvenile Warrant?",
                  valuePropName: "checked",
                  layout: "horizontal",
                  labelPostion: "label-right",
                }}
              >
                <Checkbox onChange={(e) => patchField("juvenileWarrant", e.target.checked)} />
              </SecureFormItem>
            </StyledGridCol>

            {juvenile && (
              <>
                <StyledGridCol>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANTS.FIELDS.DATE_OF_EMANCIPATION}
                    formItemProps={{ label: "Date of Emancipation", name: "dateOfEmancipation" }}
                  >
                    <CommonDatePicker
                      className="field--pink"
                      format="MM/DD/YYYY"
                      showTime={false}
                      value={form.getFieldValue("dateOfEmancipation")}
                      onChange={(d) => {
                        form.setFieldsValue({ dateOfEmancipation: d });
                        patch({ dateOfEmancipation: d ? d.format("YYYY-MM-DD") : null });
                      }}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANTS.FIELDS.DATE_OF_VIOLATION}
                    formItemProps={{ label: "Date of Violation", name: "dateOfViolation" }}
                  >
                    <CommonDatePicker
                      className="field--pink"
                      format="MM/DD/YYYY"
                      showTime={false}
                      value={form.getFieldValue("dateOfViolation")}
                      onChange={(d) => {
                        form.setFieldsValue({ dateOfViolation: d });
                        patch({ dateOfViolation: d ? d.format("YYYY-MM-DD") : null });
                      }}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </>
            )}
          </StyledGridRow>

          {isWaitFor && (
            <>
              <StyledSectionHeading>
                <span>Wait For Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16}>
                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_WAIT_FOR_INFO.FIELDS.WAIT_FOR_REASON_ID}
                    entityOverride={SCHEMAS.WARRANT_WAIT_FOR_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Wait For Reason",
                      name: "waitForReasonId",
                      rules: [{ required: true, message: "Wait For Reason is required" }],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("Warrant_WaitFor_Reason", false, false, true)}
                      onChange={(v) => patchWaitFor({ waitForReasonId: v?.value || v })}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_WAIT_FOR_INFO.FIELDS.WAIT_FOR_DATE}
                    entityOverride={SCHEMAS.WARRANT_WAIT_FOR_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Wait For Date/Time",
                      name: "waitForDateTime",
                      rules: [{ required: true, message: "Wait For Date/Time is required" }],
                    }}
                  >
                    <CommonDatePicker
                      value={form.getFieldValue("waitForDateTime")}
                      onChange={(d) => {
                        form.setFieldsValue({ waitForDateTime: d });
                        patchWaitFor({
                          waitForDate: d ? d.format("MM/DD/YYYY") : null,
                          waitForTime: d
                            ? { hour: d.hour(), minute: d.minute(), second: d.second(), nano: 0 }
                            : null,
                        });
                      }}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={12}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_WAIT_FOR_INFO.FIELDS.WAIT_FOR_BY_STAFF_ID}
                    entityOverride={SCHEMAS.WARRANT_WAIT_FOR_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Cleared By",
                      name: "waitForByStaffId",
                      rules: [{ required: true, message: "Cleared By is required" }],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("", true, true, true)}
                      onChange={(v) => patchWaitFor({ waitForByStaffId: v ? Number(v) : null })}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={24}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_WAIT_FOR_INFO.FIELDS.WAIT_FOR_COMMENTS}
                    entityOverride={SCHEMAS.WARRANT_WAIT_FOR_INFO.SCHEMA_NAME}
                    formItemProps={{ label: "Wait For Comments", name: "waitForComments" }}
                  >
                    <TextField
                      inputType="textarea"
                      onChange={(e) => patchWaitForComments(e.target.value)}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          )}

          {isCleared && (
            <>
              <StyledSectionHeading>
                <span>Clearance Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16}>
                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_CLEARANCE_INFO.FIELDS.CLEARANCE_REASON_ID}
                    entityOverride={SCHEMAS.WARRANT_CLEARANCE_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Clearance Reason",
                      name: "clearanceReasonId",
                      rules: [{ required: true, message: "Clearance Reason is required" }],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("Warrant_Clearance_Reason", false, false, true)}
                      onChange={(v) => patchClearance({ clearanceReasonId: v?.value || v })}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_CLEARANCE_INFO.FIELDS.CLEARANCE_DATE}
                    entityOverride={SCHEMAS.WARRANT_CLEARANCE_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Clearance Date/Time",
                      name: "clearanceDateTime",
                      rules: [{ required: true, message: "Clearance Date/Time is required" }],
                    }}
                  >
                    <CommonDatePicker
                      value={form.getFieldValue("clearanceDateTime")}
                      onChange={(d) => {
                        form.setFieldsValue({ clearanceDateTime: d });
                        patchClearance({
                          clearanceDate: d ? d.format("MM/DD/YYYY") : null, // fixed typo: was "MM/DD/YYY"
                          clearanceTime: d ? d.format("HH:mm") : null,
                        });
                      }}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={12}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_CLEARANCE_INFO.FIELDS.CLEARED_BY_STAFF_ID}
                    entityOverride={SCHEMAS.WARRANT_CLEARANCE_INFO.SCHEMA_NAME}
                    formItemProps={{
                      label: "Cleared By",
                      name: "clearedByStaffId",
                      rules: [{ required: true, message: "Cleared By is required" }],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("", true, true, true)}
                      onChange={(v) => patchClearance({ clearedByStaffId: v ? Number(v) : null })}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={24}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_CLEARANCE_INFO.FIELDS.CLEARANCE_COMMENTS}
                    entityOverride={SCHEMAS.WARRANT_CLEARANCE_INFO.SCHEMA_NAME}
                    formItemProps={{ label: "Clearance Comments", name: "clearanceComments" }}
                  >
                    <TextField
                      inputType="textarea"
                      onChange={(e) => patchClearanceComments(e.target.value)}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          )}
        </Form>
      </FieldSecurityProvider>
    </>
  );
};

export default WarrantInformations;
